import {
  Box,
  Button,
  Circle,
  Collapsible,
  HStack,
  Icon,
  IconButton,
  Progress,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LuCheck, LuChevronUp, LuX } from 'react-icons/lu';
import { Link as RouterLink } from 'react-router-dom';

import type { ProcessingItem, ProcessingState } from './processing-model';
import { workingStates } from './processing-model';

export interface ProcessingDockProps {
  items: ProcessingItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDismiss: () => void;
}

export function ProcessingDock({ items, open, onOpenChange, onDismiss }: ProcessingDockProps) {
  const intl = useIntl();
  const working = items.filter((item) => workingStates.has(item.state));
  const needsAction = items.filter((item) => item.state === 'needs_action');
  const failed = items.filter((item) => item.state === 'failed');

  return (
    <Box
      position="fixed"
      right={{ base: 3, md: 6 }}
      bottom={{ base: 20, md: 6 }}
      zIndex="toast"
      w={{ base: 'calc(100vw - 1.5rem)', sm: '380px' }}
      maxW="380px"
    >
      <Collapsible.Root
        open={open}
        onOpenChange={(details) => onOpenChange(details.open)}
        unmountOnExit
      >
        <Box
          bg="bg.panel"
          borderWidth="1px"
          borderColor="border.chrome"
          borderRadius="2xl"
          boxShadow="2xl"
          overflow="hidden"
        >
          <HStack gap="10px" px="14px" py="12px" align="center">
            <SummaryIcon working={working.length > 0} failed={failed.length > 0} />

            <Box flex="1 1 0" minW={0} role="status" aria-live="polite">
              <Text truncate fontSize="13.5px" fontWeight="bold" color="fg">
                {working.length > 0 ? (
                  <FormattedMessage
                    id="34699ff7-c83c-4e58-8d34-aecb144c451a"
                    defaultMessage="{count, plural, one {Processing # item} other {Processing # items}}"
                    values={{ count: working.length }}
                  />
                ) : failed.length > 0 ? (
                  <FormattedMessage
                    id="5978baa3-b1c2-4310-9a02-49f964d3585e"
                    defaultMessage="{count, plural, one {# item needs attention} other {# items need attention}}"
                    values={{ count: failed.length }}
                  />
                ) : (
                  <FormattedMessage
                    id="4107bd44-2ae2-4a1c-818e-343f738cca4c"
                    defaultMessage="{count, plural, one {# item ready} other {# items ready}}"
                    values={{ count: items.length }}
                  />
                )}
              </Text>
              <Text
                mt="2px"
                truncate
                fontSize="11.5px"
                fontWeight="medium"
                color={needsAction.length > 0 || working.length > 0 ? 'gold.link' : 'fg.muted'}
              >
                {needsAction.length > 0 ? (
                  <FormattedMessage
                    id="3f37c9be-b4d7-4452-bff6-63c42f043741"
                    defaultMessage="{count, plural, one {# ready for you} other {# ready for you}}"
                    values={{ count: needsAction.length }}
                  />
                ) : working.length > 0 ? (
                  <FormattedMessage
                    id="8d5c008d-664d-44af-b995-db098663d056"
                    defaultMessage="Keep working — we'll notify you"
                  />
                ) : failed.length > 0 ? (
                  <FormattedMessage
                    id="595cbfe0-1b06-4f43-89b0-112ce85a9e41"
                    defaultMessage="Open an item to review or try again"
                  />
                ) : (
                  <FormattedMessage
                    id="fed4d403-ebea-4839-ae26-bae96585ffbc"
                    defaultMessage="Everything is up to date"
                  />
                )}
              </Text>
            </Box>

            <Collapsible.Trigger asChild>
              <IconButton
                variant="ghost"
                size="xs"
                borderRadius="full"
                color="fg.muted"
                flexShrink={0}
                aria-label={intl.formatMessage(
                  open
                    ? {
                        id: 'a0b2df9e-b561-4c48-882d-548420384f4e',
                        defaultMessage: 'Hide processing items',
                      }
                    : {
                        id: '0557681e-fb18-4941-89c8-81200771229a',
                        defaultMessage: 'Show processing items',
                      },
                )}
              >
                <Icon
                  as={LuChevronUp}
                  boxSize="15px"
                  transform={open ? 'rotate(180deg)' : undefined}
                  transition="transform 0.16s ease"
                />
              </IconButton>
            </Collapsible.Trigger>

            <IconButton
              variant="ghost"
              size="xs"
              borderRadius="full"
              color="fg.muted"
              flexShrink={0}
              onClick={onDismiss}
              aria-label={intl.formatMessage({
                id: 'e876523e-b342-4db7-aef2-c3fdd28c86d8',
                defaultMessage: 'Dismiss processing',
              })}
            >
              <Icon as={LuX} boxSize="15px" />
            </IconButton>
          </HStack>

          <Collapsible.Content>
            <Box borderTopWidth="1px" borderColor="border.chrome" maxH="264px" overflowY="auto">
              {items.map((item) => <ProcessingRow key={item.key} item={item} />)}
            </Box>
          </Collapsible.Content>
        </Box>
      </Collapsible.Root>
    </Box>
  );
}

function SummaryIcon({ working, failed }: { working: boolean; failed: boolean }) {
  if (working) return <Spinner size="sm" borderWidth="2px" color="gold.solid" flexShrink={0} />;
  return (
    <Circle
      size="7"
      flexShrink={0}
      bg={failed ? 'red.subtle' : 'green.subtle'}
      color={failed ? 'red.fg' : 'green.fg'}
    >
      <Icon as={failed ? LuX : LuCheck} boxSize="15px" aria-hidden />
    </Circle>
  );
}

function ProcessingRow({ item }: { item: ProcessingItem }) {
  const working = workingStates.has(item.state);
  return (
    <Box
      px="14px"
      py="11px"
      borderBottomWidth="1px"
      borderColor="border.chrome"
      _last={{ borderBottomWidth: 0 }}
    >
      <HStack gap="11px" align="flex-start">
        <RowIcon state={item.state} />
        <Box flex="1 1 0" minW={0}>
          <Text truncate fontSize="13px" fontWeight="semibold" color="fg">
            {item.title}
          </Text>
          <Text
            mt="3px"
            fontSize="11.5px"
            fontWeight="medium"
            color={item.state === 'failed' ? 'fg.error' : 'fg.muted'}
          >
            {item.detail}
          </Text>
          {working && (
            <Progress.Root mt="8px" size="xs" value={null} colorPalette="gold">
              <Progress.Track borderRadius="full" bg="bg.chip">
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          )}
        </Box>
        {item.primaryAction && <RowAction action={item.primaryAction} />}
      </HStack>
    </Box>
  );
}

function RowIcon({ state }: { state: ProcessingState }) {
  if (workingStates.has(state)) {
    return (
      <Circle size="7" flexShrink={0} bg="gold.washMuted" color="gold.ink">
        <Spinner size="xs" borderWidth="2px" />
      </Circle>
    );
  }
  const bad = state === 'failed';
  return (
    <Circle
      size="7"
      flexShrink={0}
      bg={bad ? 'red.subtle' : 'green.subtle'}
      color={bad ? 'red.fg' : 'green.fg'}
    >
      <Icon as={bad ? LuX : LuCheck} boxSize="14px" aria-hidden />
    </Circle>
  );
}

function RowAction({ action }: { action: NonNullable<ProcessingItem['primaryAction']> }) {
  if (action.href) {
    return (
      <Button asChild size="xs" variant="outline" borderRadius="full" flexShrink={0}>
        <RouterLink to={action.href} aria-label={action.ariaLabel}>
          {action.label}
        </RouterLink>
      </Button>
    );
  }

  return (
    <Button
      size="xs"
      variant="outline"
      borderRadius="full"
      flexShrink={0}
      onClick={action.onInvoke}
      loading={action.loading}
      aria-label={action.ariaLabel}
    >
      {action.label}
    </Button>
  );
}
